import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import formatImageSrc from '../../lib/formatImageSrc';
import { StarRatingInput, formatRating } from '../StarRating';

// API base is centralized in lib/api

// Special value understood by the backend edit_review handler to mean
// "explicitly remove the existing photo for this review".
const PHOTO_DELETE_SENTINEL = '__DELETE_REVIEW_PHOTO__';

const EditReviewModal = ({ isOpen, onClose, review, onSave }) => {
  const [editedReview, setEditedReview] = useState({
    rating: 5.0,
    review_text: ''
  });
  const [existingImageUrl, setExistingImageUrl] = useState(null);
  // 'unchanged' | 'replace' | 'remove'
  const [photoAction, setPhotoAction] = useState('unchanged');
  const [newPhotoData, setNewPhotoData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Pre-populate form when modal opens
  useEffect(() => {
    if (isOpen && review) {
      setEditedReview({
        rating: typeof review.rating === 'number' ? review.rating : 5.0,
        review_text: review.review_text || ''
      });

      // Prefer the signed image URL if present; fall back to any legacy photo field
      const fromImageUrl = review.image_url ? formatImageSrc(review.image_url) : null;
      const fromPhoto = review.photo ? formatImageSrc(review.photo) : null;
      setExistingImageUrl(fromImageUrl || fromPhoto || null);
      setPhotoAction('unchanged');
      setNewPhotoData(null);
    }
  }, [isOpen, review]);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (ev) => {
      const base64 = ev.target.result;
      setNewPhotoData(base64);
      setPhotoAction('replace');
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!review) return;

    const reviewId = review.review_id || review.id;
    if (!reviewId) {
      console.error('EditReviewModal: Missing review identifier for review:', review);
      alert('We could not determine which review to update. Please refresh the page and try again.');
      return;
    }

    // Validate review text length
    const trimmedText = (editedReview.review_text || '').trim();
    if (trimmedText && trimmedText.length > 1000) {
      alert('Review text cannot exceed 1000 characters');
      return;
    }

    // Prepare update data - only include changed fields (declared outside try for catch block access)
    const updateData = {};
    if (typeof editedReview.rating === 'number' && editedReview.rating !== review.rating) {
      updateData.rating = editedReview.rating;
    }

    const originalText = (review.review_text || '').trim();
    if (trimmedText !== originalText) {
      // Allow clearing the review text by sending an empty string
      updateData.review_text = trimmedText;
    }

    if (photoAction === 'replace' && newPhotoData) {
      updateData.photo = newPhotoData;
    } else if (photoAction === 'remove') {
      updateData.photo = PHOTO_DELETE_SENTINEL;
    }

    if (Object.keys(updateData).length === 0) {
      alert('No changes to save');
      return;
    }

    setLoading(true);
    try {
      await api.patch(
        `/review/${reviewId}`,
        updateData
      );

      alert('Review updated successfully!');
      onSave(); // Refresh data
      onClose();
    } catch (error) {
      console.error('Error updating review:', {
        error,
        reviewId: review.review_id || review.id,
        updateData
      });
      if (error.response?.status === 403) {
        alert('You can only edit your own reviews');
      } else if (error.response?.status === 400) {
        const detail = error.response.data?.detail;
        let message = 'Failed to update review. Please try again.';

        // Check if this is a moderation rejection
        if (detail) {
          const isModeration = detail.action === 'rejected' || detail.violations;
          if (isModeration) {
            // Determine if it was the photo or text that was flagged
            const field = detail.field;
            if (field === 'photo') {
              message = 'This photo may violate our community guidelines. Please choose a different image.';
            } else if (field === 'review_text') {
              message = 'Your review text may violate our community guidelines. Please edit it and try again.';
            } else {
              message = 'Your content may violate our community guidelines. Please review and try again.';
            }
          } else if (typeof detail === 'string') {
            message = detail;
          } else if (detail.message) {
            message = detail.message;
          }
        }

        alert(message);
      } else {
        alert('Failed to update review. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const removePhoto = () => {
    setPhotoAction('remove');
    setNewPhotoData(null);
    setExistingImageUrl(null);
  };

  if (!isOpen || !review) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      ></div>
      
      {/* Modal Content */}
      <div className="relative bg-white w-full max-w-2xl max-h-[90vh] rounded-2xl shadow-2xl overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white p-6 border-b border-gray-200 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold text-gray-900">Edit Review</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-light"
            >
              ×
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            Editing review for "{review.sandwich?.name}" at {review.restaurant?.name}
          </p>
        </div>
        
        {/* Form Content */}
        <div className="p-6 space-y-6">
          {/* Rating */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rating
            </label>
            <div className="flex items-center gap-2">
              <StarRatingInput
                value={editedReview.rating}
                onChange={(next) => setEditedReview((prev) => ({ ...prev, rating: next }))}
                ariaLabel="Edit rating"
              />
              <span className="text-sm text-gray-600">
                {formatRating(editedReview.rating)} star{editedReview.rating !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          {/* Review Text */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Review (optional)
            </label>
            <textarea
              value={editedReview.review_text}
              onChange={(e) => setEditedReview(prev => ({
                ...prev,
                review_text: e.target.value
              }))}
              placeholder="Share your thoughts about this sandwich..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-passion-red focus:border-transparent"
              maxLength={1000}
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Optional but recommended</span>
              <span>{editedReview.review_text.length}/1000</span>
            </div>
          </div>

          {/* Photo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Photo (optional)
            </label>

            {(() => {
              const currentImageSrc =
                photoAction === 'replace'
                  ? (newPhotoData ? formatImageSrc(newPhotoData) : null)
                  : existingImageUrl || null;

              if (currentImageSrc) {
                return (
                  <div className="relative max-w-sm">
                    <div className="aspect-[4/3] overflow-hidden rounded-lg">
                      <img
                        src={currentImageSrc}
                        alt="Review"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={removePhoto}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 transition-colors"
                    >
                      ×
                    </button>
                  </div>
                );
              }

              return (
                <label className="border-2 border-dashed border-gray-300 rounded-lg p-6 cursor-pointer hover:border-passion-red transition-colors block">
                  <div className="text-center">
                    <span className="text-gray-400 text-2xl">📷</span>
                    <p className="text-sm text-gray-600 mt-2">Click to upload a new photo</p>
                    <p className="text-xs text-gray-500 mt-1">Max 5MB, JPG/PNG</p>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                  />
                </label>
              );
            })()}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white p-6 border-t border-gray-200 rounded-b-2xl">
          <div className="flex space-x-4">
            <button
              onClick={onClose}
              className="flex-1 py-3 text-gray-600 hover:text-gray-800 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex-1 py-3 bg-passion-red text-white font-medium rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditReviewModal;


